import { useEffect, useState } from 'react';
import Navigation from './components/Navigation';
import HomePage from './components/HomePage';
import AboutPage from './components/AboutPage';
import HistoryPage from './components/HistoryPage';
import DevelopmentPage from './components/DevelopmentPage';
import CommunityPage from './components/CommunityPage';
import UpdatesPage from './components/UpdatesPage';
import DataExplorerPage from './components/DataExplorerPage';
import TypesPage from './components/TypesPage';
import GalleryPage from './components/GalleryPage';
import ContactPage from './components/ContactPage';
import { Leaf, Mail, Phone, MapPin, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';
// src/lib/leaflet-config.ts
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});


export default function App() {
  const [currentPage, setCurrentPage] = useState('home');

   useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);
  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <HomePage onPageChange={setCurrentPage} />;
      case 'about':
        return <AboutPage onPageChange={setCurrentPage}/>;
      case 'history':
        return <HistoryPage />;
      case 'development':
        return <DevelopmentPage />;
      case 'community':
        return <CommunityPage />;
      case 'updates':
        return <UpdatesPage />;
      case 'data-explorer':
        return <DataExplorerPage />;
      case 'types':
        return <TypesPage onPageChange={setCurrentPage}/>;
      case 'gallery':
        return <GalleryPage />;
      case 'contact':
        return <ContactPage />;
      default:
        return <HomePage onPageChange={setCurrentPage} />;
    }
  };

  const quickLinks = [
    { id: 'about', label: 'About Us' },
    { id: 'history', label: 'Our History' },
    { id: 'updates', label: 'Latest Updates' },
    { id: 'data-explorer', label: 'Data Explorer' },
  ];

  const resources = [
    { id: 'types', label: 'Types of Sugar' },
    { id: 'development', label: 'Development Impact' },
    { id: 'community', label: 'Community Programs' },
    { id: 'gallery', label: 'Photo Gallery' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation currentPage={currentPage} onPageChange={setCurrentPage} />
      <main>
        {renderPage()}
      </main>
      
      {/* Footer */}
      <footer className="bg-forest-green text-white relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 nature-pattern opacity-10"></div>
        
        <div className="relative z-10">
          {/* Main footer content */}
          <div className="py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {/* Brand section */}
                <div className="lg:col-span-1">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="relative">
                      <div className="w-12 h-12 bg-leaf-green rounded-2xl flex items-center justify-center">
                        <Leaf className="w-6 h-6 text-white" />
                      </div>
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-golden-harvest rounded-full border-2 border-forest-green"></div>
                    </div>
                    <div>
                      <div className="font-bold text-xl">ACP/LDC</div>
                      <div className="text-sm text-leaf-green/80">Sugar Industries</div>
                    </div>
                  </div>
                  <p className="text-white/80 text-sm leading-relaxed mb-6">
                    Representing sugar industries across African, Caribbean, Pacific and Least Developed Countries 
                    since the 1950s, fostering sustainable development and fair trade practices.
                  </p>
                  <div className="flex space-x-4">
                    <div className="w-10 h-10 bg-white/10 hover:bg-leaf-green rounded-xl flex items-center justify-center cursor-pointer transition-all duration-300 hover:scale-110">
                      <Linkedin className="w-5 h-5" />
                    </div>
                    <div className="w-10 h-10 bg-white/10 hover:bg-leaf-green rounded-xl flex items-center justify-center cursor-pointer transition-all duration-300 hover:scale-110">
                      <Twitter className="w-5 h-5" />
                    </div>
                    <div className="w-10 h-10 bg-white/10 hover:bg-leaf-green rounded-xl flex items-center justify-center cursor-pointer transition-all duration-300 hover:scale-110">
                      <Facebook className="w-5 h-5" />
                    </div>
                    <div className="w-10 h-10 bg-white/10 hover:bg-leaf-green rounded-xl flex items-center justify-center cursor-pointer transition-all duration-300 hover:scale-110">
                      <Instagram className="w-5 h-5" />
                    </div>
                  </div>
                </div>
                
                {/* Quick Links */}
                <div>
                  <h3 className="font-bold text-lg mb-6 text-white">Quick Links</h3>
                  <ul className="space-y-3">
                    {quickLinks.map((link) => (
                      <li key={link.id}>
                        <button 
                          onClick={() => setCurrentPage(link.id)} 
                          className="text-white/80 hover:text-leaf-green transition-colors duration-200 text-sm"
                        >
                          {link.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Resources */}
                <div>
                  <h3 className="font-bold text-lg mb-6 text-white">Resources</h3>
                  <ul className="space-y-3">
                    {resources.map((resource) => (
                      <li key={resource.id}>
                        <button 
                          onClick={() => setCurrentPage(resource.id)} 
                          className="text-white/80 hover:text-leaf-green transition-colors duration-200 text-sm"
                        >
                          {resource.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Contact */}
                <div>
                  <h3 className="font-bold text-lg mb-6 text-white">Contact Info</h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Mail className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-white/80 text-sm">info@acpsugar.org</p>
                        <p className="text-white/60 text-xs">General inquiries</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Phone className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-white/80 text-sm">+44 (0) 20 7XXX XXXX</p>
                        <p className="text-white/60 text-xs">Business hours: 9AM-5PM GMT</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                        <MapPin className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-white/80 text-sm">London, United Kingdom</p>
                        <button 
                          onClick={() => setCurrentPage('contact')} 
                          className="text-leaf-green hover:text-golden-harvest text-xs transition-colors underline"
                        >
                          View contact form
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Bottom bar */}
          <div className="border-t border-white/10 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                <p className="text-white/60 text-sm">
                  © 2025 ACP/LDC Sugar Industries Group. All rights reserved.
                </p>
                <div className="flex space-x-6 text-sm">
                  <button className="text-white/60 hover:text-leaf-green transition-colors">Privacy Policy</button>
                  <button className="text-white/60 hover:text-leaf-green transition-colors">Terms of Service</button>
                  <button className="text-white/60 hover:text-leaf-green transition-colors">Cookie Policy</button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-leaf-green/10 rounded-full blur-3xl"></div>
        <div className="absolute top-0 right-0 w-40 h-40 bg-golden-harvest/10 rounded-full blur-3xl"></div>
      </footer>
    </div>
  );
}